import React from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import arrowLeftSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-left-small';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../utils/constants';

/**
 * Props for {@link PublishedAssignmentHeader}.
 *
 * The five plan-metadata strings are passed as plain strings; the container
 * is responsible for sourcing them (`submittedBy` / `submittedDate` from the
 * `GetStationAssignmentsResponse`'s `lastModifiedBy` / `lastModifiedDate`, and
 * `planId` from the URL path parameter — see design.md → "Plan-metadata
 * fields in the header").
 */
export interface PublishedAssignmentHeaderProps {
  planId: string;
  submittedBy: string;
  submittedDate: string;
  ofdDate: string;
  clockWindow: string;
}

/**
 * Header sub-component for the Published Assignment Screen.
 *
 * Renders:
 *   - A page title that identifies the screen as the published view of a
 *     station assignment plan ("Published station assignment").
 *   - A back-navigation control whose `onClick` calls
 *     `navigate(ROUTES.STATION_ASSIGNMENT)` (Req 7.2).
 *   - The five plan-metadata fields (submitted-by, submitted-date, OFD-date,
 *     clock-window, Plan_Id) using a compact label/value layout (Req 7.5).
 *
 * Visual parity with the editable Assignments page is achieved by reusing
 * the same Meridian primitives (`Box`, `Heading`, `Button`, `Text`) and the
 * `station-assignment-header` / `header-left` / `header-actions` SCSS
 * classes from `StationAssignmentTabs.scss` (the surface that hosts the
 * editable Assignments page's header). No "Read-only" badge or banner is
 * rendered — see task 8 of the spec for the rationale.
 */
const PublishedAssignmentHeader: React.FC<PublishedAssignmentHeaderProps> = ({
  planId,
  submittedBy,
  submittedDate,
  ofdDate,
  clockWindow,
}) => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(ROUTES.SHIFT_ROTATION_HISTORY);
  };

  const handleAssociateViewClick = () => {
    navigate(ROUTES.SHIFT_ROTATION_ASSOCIATE_VIEW);
  };

  return (
    <Box
      className="station-assignment-header published-assignment-header"
      data-testid="published-assignment-header"
    >
      <div className="header-left">
        <Button
          type="tertiary"
          size="small"
          onClick={handleBackClick}
          data-testid="published-assignment-header-back-button"
        >
          <Icon tokens={arrowLeftSmallTokens}>Back</Icon>
        </Button>
        <Heading type="h400" level={1}>
          Published station assignment
        </Heading>
      </div>
      <Box className="header-actions">
        <Button
          type="secondary"
          onClick={handleAssociateViewClick}
          data-testid="published-assignment-associate-view-button"
        >
          Associate view
        </Button>
        <div className="published-assignment-header__metadata">
          <div
            className="published-assignment-header__metadata-item"
            data-testid="published-assignment-header-submitted-by"
          >
            <Text type="b100" color="secondary">
              Submitted by
            </Text>
            <Text type="b200">{submittedBy}</Text>
          </div>
          <div
            className="published-assignment-header__metadata-item"
            data-testid="published-assignment-header-submitted-date"
          >
            <Text type="b100" color="secondary">
              Submitted date
            </Text>
            <Text type="b200">{submittedDate}</Text>
          </div>
          <div
            className="published-assignment-header__metadata-item"
            data-testid="published-assignment-header-ofd-date"
          >
            <Text type="b100" color="secondary">
              OFD date
            </Text>
            <Text type="b200">{ofdDate}</Text>
          </div>
          <div
            className="published-assignment-header__metadata-item"
            data-testid="published-assignment-header-clock-window"
          >
            <Text type="b100" color="secondary">
              Clock window
            </Text>
            <Text type="b200">{clockWindow}</Text>
          </div>
          <div
            className="published-assignment-header__metadata-item"
            data-testid="published-assignment-header-plan-id"
          >
            <Text type="b100" color="secondary">
              Plan_Id
            </Text>
            <Text type="b200">{planId}</Text>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default PublishedAssignmentHeader;
